import React from 'react'
import {
  Autocomplete,
  Button,
  Chip,
  Grid,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { createUser } from 'src/services/IAM/AdminUser/AdminUser.service'
import GlobalLoading from 'src/components/Loadings/GlobalLoading'
import useLoading from 'src/hooks/useLoading'
import { useAlert } from 'src/hooks/useAlert'
import { IGroup } from 'src/services/IAM/AdminGroup/AdminGroup.interface'
import AlertDialog from 'src/components/Dialog'
import StepperModal from './StepperModal'
import { useUserIAM } from 'src/hooks/IAM/AdminUser/useUserIAM'
import { useAdminPermission } from 'src/hooks/IAM/AdminPermission/useAdminPermission'
import { useAdminGroup } from 'src/hooks/IAM/AdminGroups/useAdminGroups'
/**
 * CreateUser Component
 *
 * This component provides a form for creating a new user within an IAM (Identity 
 * and Access Management) system. It leverages Material UI components for the 
 * user interface and supports multiple fields such as user document type, 
 * document ID, name, email, phone, and associated user groups and permissions.
 *
 * Features:
 * - Form inputs for various user attributes, including document type, document ID,
 *   first and last name, email, phone, and user type.
 * - Autocomplete for selecting document types, user types, and multiple groups.
 * - Integration with a permissions management dialog to allow the selection of 
 *   user permissions.
 * - Utilizes the `StepperModal` component for handling permissions and `AlertDialog` 
 *   for opening a modal to add permissions.
 * - Displays loading feedback through the `GlobalLoading` component.
 * - Alerts the user when the creation is successful or if an error occurs, using
 *   the `useAlert` hook.

 * Hooks Used:
 * - `useAdminGroup`: Provides the groups available for selection.
 * - `useUserIAM`: Manages the user information, including setting and clearing user data.
 * - `useAdminPermission`: Manages the permissions, including loading permissions
 *   and managing selected permissions.
 * - `useLoading`: Provides loading state management, including showing and hiding
 *   a global loading spinner.
 * - `useAlert`: Displays alerts on success or error.
 *
 * Form Fields:
 * - `Tipo de documento`: Autocomplete for selecting the document type.
 * - `Documento ID`: Input for entering the user document ID.
 * - `Nombre`: Input for entering the user's first name.
 * - `Apellido`: Input for entering the user's last name.
 * - `Correo`: Input for entering the user's email.
 * - `Celular`: Input for entering the user's phone number.
 * - `Tipo de usuario`: Autocomplete for selecting the type of user (e.g., backoffice, client).
 * - `Grupos`: Autocomplete for selecting user groups, with the ability to select 
 *   multiple groups.
 * - `Permisos`: Button that opens a dialog for selecting permissions through the 
 *   `StepperModal` component.
 *
 * Example Usage:
 * @example
 * ```tsx
 * return <CreateUser />
 * ```
 * Submission:
 * - On form submission, the `handleSubmit` function sends the user data, including 
 *   selected permissions, to the `createUser` service. The user data is then cleared 
 *   on success, and an alert is shown to indicate the result.
 *
 * @component
 */

const CreateUser = () => {
  const { groups } = useAdminGroup()
  const { userIAM, setUserIAMAdmin, clearUserIAM, loadUsers } = useUserIAM()
  const {
    permissions,
    loadPermissions,
    selectedPermissions,
    setSelectPermission,
  } = useAdminPermission()
  const { showAlert } = useAlert()
  const { isLoading, startLoading, stopLoading } = useLoading()
  const kindOfDocuments = ['', 'CC', 'CE', 'NIT']
  const kindOfUser = ['', 'backoffice', 'client']
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    try {
      startLoading()
      await createUser({
        ...userIAM,
        permissions: selectedPermissions,
      })
      showAlert('success', 'Usuario creado correctamente')
      loadUsers({})
      clearUserIAM()
    } catch (error) {
      showAlert('error', String(error))
    } finally {
      stopLoading()
    }
  }

  return (
    <div>
      {isLoading && <GlobalLoading />}
      <Typography variant="h3" textAlign={'center'} marginBottom={4}>
        Crear usuario
      </Typography>
      <form onSubmit={handleSubmit}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <Autocomplete
              id="combo-box-demo"
              defaultValue={''}
              options={kindOfDocuments}
              getOptionLabel={(option) => option}
              value={userIAM.userDocType ?? ''}
              onChange={(event, newValue: string | null) =>
                setUserIAMAdmin({ ...userIAM, userDocType: newValue ?? '' })
              }
              renderInput={(params) => (
                <TextField {...params} label="Tipo de documento" />
              )}
              renderOption={(props, option) =>
                option === '' ? null : (
                  <li {...props} key={option}>
                    {option}
                  </li>
                )
              }
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              id="userDocId"
              label="Documento ID"
              type="text"
              fullWidth
              value={userIAM.userDocId}
              onChange={(e) =>
                setUserIAMAdmin({ ...userIAM, userDocId: e.target.value })
              }
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              id="firstName"
              label="Nombre"
              type="text"
              fullWidth
              value={userIAM.firstName}
              onChange={(e) =>
                setUserIAMAdmin({ ...userIAM, firstName: e.target.value })
              }
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              id="lastName"
              label="Apellido"
              type="text"
              fullWidth
              value={userIAM.lastName}
              onChange={(e) =>
                setUserIAMAdmin({ ...userIAM, lastName: e.target.value })
              }
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              id="email"
              label="Correo"
              type="email"
              fullWidth
              value={userIAM.email}
              onChange={(e) =>
                setUserIAMAdmin({ ...userIAM, email: e.target.value })
              }
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              id="phone"
              label="Celular"
              type="tel"
              fullWidth
              value={userIAM.phone}
              onChange={(e) =>
                setUserIAMAdmin({ ...userIAM, phone: e.target.value })
              }
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <Autocomplete
              id="combo-box-demo"
              defaultValue={''}
              options={kindOfUser}
              getOptionLabel={(option) => option}
              value={userIAM.type ?? ''}
              renderInput={(params) => (
                <TextField {...params} label="Tipo de usuario" />
              )}
              onChange={(event, newValue: string | null) => {
                setUserIAMAdmin({ ...userIAM, type: newValue ?? '' })
              }}
              renderOption={(props, option) =>
                option === '' ? null : (
                  <li {...props} key={option}>
                    {option}
                  </li>
                )
              }
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <Autocomplete
              sx={{
                flex: 1,
              }}
              disablePortal
              id="combo-box-demo"
              options={groups ?? []}
              multiple
              value={userIAM.groups ?? []}
              onChange={(event, newValue: IGroup[]) =>
                setUserIAMAdmin({ ...userIAM, groups: newValue })
              }
              getOptionLabel={(option) => `${option.groupName}`}
              renderTags={(tagValue, getTagProps) =>
                tagValue.map((option: IGroup, index) => (
                  <Chip
                    label={`${option.groupName}`}
                    {...getTagProps({ index })}
                    key={index}
                  />
                ))
              }
              isOptionEqualToValue={(option, value) => {
                return option.groupName === value.groupName
              }}
              renderInput={(params) => <TextField {...params} label="Grupos" />}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <AlertDialog
              title="Permisos"
              buttonLabel={`Agregar permisos: ${selectedPermissions.length}`}
              maxWidth="lg"
              children={
                <Stack direction={'row'}>
                  <StepperModal
                    handlerSelectedPermissions={setSelectPermission}
                    selectedPermissions={selectedPermissions}
                    loadPermissions={loadPermissions}
                    permissionsProp={permissions}
                  />
                </Stack>
              }
            />
          </Grid>
        </Grid>
        <Button
          sx={{
            width: {
              xs: '100%',
              md: '250px',
            },
            mt: 2,
            height: '50px',
          }}
          type="submit"
          variant="contained"
        >
          Crear
        </Button>
      </form>
    </div>
  )
}

export default CreateUser
